'use client'

import * as React from 'react'
import type { JSONValue, WidenPrimitives } from '@growthbook/growthbook-react'
import {
  GrowthBook,
  GrowthBookProvider as InternalGrowthBookProvider,
  useFeatureValue as InternalUseFeatureValue,
  useFeatureIsOn as InternalUseFeatureIsOn,
  useGrowthBook as InternalUseGrowthBook,
  BrowserCookieStickyBucketService,
} from '@growthbook/growthbook-react'
import { isSystemDarkMode } from 'utils/theme'
import {
  AmplitudeEvents,
  sendAmplitudeEvent,
  getAmplitudeSessionId,
} from '@repo/analytics/amplitude/utils'
import { invariant } from 'utils/invariant'
import { AMPLITUDE_DEVICE_ID_COOKIE_NAME } from '@repo/analytics/amplitude'
import { getCookie } from 'utils/dom'
import { useUserQuery } from 'auth/hooks'
import { useLangCookie } from 'cf-i18n/cookies'
import type { Language } from 'codegen/types'
import { defaultLanguage } from 'cf-i18n/constants'
import {
  SUBSCRIPTION_STATUS_VALUES_WITH_PENDING_CANCEL,
  TRIAL_ACTIVE,
  PAID_SUBSCRIPTION_ACTIVE,
  COUNTRY_COOKIE_NAME,
  REGION_COOKIE_NAME,
  GROWTHBOOK_DISABLE_ALL_FEATURES_COOKIE_NAME,
  GROWTHBOOK_ENABLED_FEATURES_COOKIE_NAME,
  SUBSCRIPTION_STATUS_COOKIE_NAME,
  USER_ID_COOKIE_NAME,
  WORDPRESS_LOGIN_COOKIE_NAME,
  STUDIO_AI_SUBSCRIPTION_STATUS_COOKIE_NAME,
  COIN_SUBSCRIPTION_STATUS_COOKIE_NAME,
} from 'constants/cookies'
import Cookies from 'js-cookie'

import { isBrowser } from './utils/is-browser'
import type { DeviceType } from './utils/device-data'
import { getDeviceType, getOS, getBrowserType } from './utils/device-data'
import { localStore, sessionStore } from 'utils/storage'

const GROWTHBOOK_CLIENT_KEY =
  process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY || process.env.VITE_GROWTHBOOK_CLIENT_KEY

invariant(GROWTHBOOK_CLIENT_KEY, 'GROWTHBOOK_CLIENT_KEY is required')

const isProduction = process.env.VITE_PUBLIC_ENV === 'production'

declare global {
  interface Window {
    __gbInitialPayload?: string
    earlyGrowthBook?: GrowthBook
  }
}

// In addition to passing attributes into the SDK, you also must update the GrowthBook App with these same attribute keys.
// You can do this under SDK Connections → Attributes
// The actual values of the targeting attributes are never sent to GrowthBook. They are only stored in memory locally within the SDK.
interface GrowthbookAttributes {
  id: string | null
  deviceId: string | null
  isLoggedIn: boolean
  hasAllAccess: boolean
  hasAllAccessTrial: boolean
  hasAllAccessPaidSub: boolean
  hasStudioAiSubscription: boolean
  hasStudioAiTrial: boolean
  hasStudioAiPaidSub: boolean
  hasCoinSubscription: boolean
  email: string | null
  systemTheme: 'dark' | 'light'
  os: string
  country: string
  region: string
  language: Language
  deviceType: DeviceType
  browser: string
  referrer: string
}

type GrowthbookAdditionalAttributes = Record<string, unknown> & {
  [K in keyof GrowthbookAttributes]?: never
}

interface GrowthBookOptions {
  streaming?: boolean
}

interface GrowthBookProviderProps {
  children: React.ReactNode
  additionalAttributes?: GrowthbookAdditionalAttributes
  options?: GrowthBookOptions
}

export function GrowthBookProvider({
  children,
  additionalAttributes,
  options,
}: GrowthBookProviderProps) {
  if (isBrowser()) {
    return (
      <GrowthBookProviderBrowser additionalAttributes={additionalAttributes} options={options}>
        {children}
      </GrowthBookProviderBrowser>
    )
  }

  return <GrowthBookProviderServer>{children}</GrowthBookProviderServer>
}

function GrowthBookProviderServer({ children }: GrowthBookProviderProps) {
  return (
    <InternalGrowthBookProvider growthbook={new GrowthBook()}>
      {children}
    </InternalGrowthBookProvider>
  )
}

// oxlint-disable-next-line complexity
function GrowthBookProviderBrowser({
  children,
  additionalAttributes,
  options,
}: GrowthBookProviderProps) {
  const isLoggedIn = !!getCookie(WORDPRESS_LOGIN_COOKIE_NAME)
  const userId = getCookie(USER_ID_COOKIE_NAME)
  const deviceId = getCookie(AMPLITUDE_DEVICE_ID_COOKIE_NAME)
  const country = getCookie(COUNTRY_COOKIE_NAME)
  const region = getCookie(REGION_COOKIE_NAME)
  //All Access
  const hasAllAccess = Object.values(SUBSCRIPTION_STATUS_VALUES_WITH_PENDING_CANCEL).includes(
    getCookie(SUBSCRIPTION_STATUS_COOKIE_NAME),
  )
  const hasAllAccessTrial = Object.values(TRIAL_ACTIVE).includes(
    getCookie(SUBSCRIPTION_STATUS_COOKIE_NAME),
  )
  const hasAllAccessPaidSub = Object.values(PAID_SUBSCRIPTION_ACTIVE).includes(
    getCookie(SUBSCRIPTION_STATUS_COOKIE_NAME),
  )

  //Studio AI
  const hasStudioAiSubscription = Object.values(
    SUBSCRIPTION_STATUS_VALUES_WITH_PENDING_CANCEL,
  ).includes(getCookie(STUDIO_AI_SUBSCRIPTION_STATUS_COOKIE_NAME))
  const hasStudioAiTrial = Object.values(TRIAL_ACTIVE).includes(
    getCookie(STUDIO_AI_SUBSCRIPTION_STATUS_COOKIE_NAME),
  )
  const hasStudioAiPaidSub = Object.values(PAID_SUBSCRIPTION_ACTIVE).includes(
    getCookie(STUDIO_AI_SUBSCRIPTION_STATUS_COOKIE_NAME),
  )

  const hasCoinSubscription = Object.values(
    SUBSCRIPTION_STATUS_VALUES_WITH_PENDING_CANCEL,
  ).includes(getCookie(COIN_SUBSCRIPTION_STATUS_COOKIE_NAME))

  const { data = { email: null } } = useUserQuery(undefined, {
    select: data => ({
      email: data?.me?.user?.email ?? null,
    }),
  })

  const { email } = data

  const systemTheme = isSystemDarkMode() ? 'dark' : 'light'
  const [langCookie] = useLangCookie()

  const language = React.useMemo(() => {
    if (!isBrowser()) {
      return langCookie || defaultLanguage
    }

    return (
      langCookie ||
      document?.currentScript?.getAttribute('lang') ||
      document?.documentElement?.lang?.split('-')[0] ||
      defaultLanguage
    )
  }, [langCookie])

  //Will be using the referrer to target users based on where they came from
  const referrer = React.useMemo(() => {
    if (!isBrowser()) {
      return ''
    }

    return document.referrer
  }, [])

  // Build the current attributes object whenever dependencies change
  const attributes = React.useMemo((): GrowthbookAttributes => {
    return {
      id: userId ?? null,
      deviceId: deviceId ?? null,
      isLoggedIn: isLoggedIn ?? false,
      hasAllAccess,
      hasAllAccessTrial,
      hasAllAccessPaidSub,
      hasStudioAiSubscription,
      hasStudioAiTrial,
      hasStudioAiPaidSub,
      hasCoinSubscription,
      country,
      region: region,
      email,
      systemTheme,
      os: getOS(),
      deviceType: getDeviceType(),
      browser: getBrowserType(),
      language: language as Language,
      referrer,
    }
  }, [
    deviceId,
    hasAllAccess,
    hasAllAccessPaidSub,
    hasAllAccessTrial,
    hasStudioAiSubscription,
    hasStudioAiTrial,
    hasStudioAiPaidSub,
    hasCoinSubscription,
    isLoggedIn,
    language,
    referrer,
    systemTheme,
    userId,
    email,
    country,
    region,
  ])

  // Initialize GrowthBook instance if it doesn't exist yet
  // We use window instead of useRef because we want to ensure that the GrowthBook instance is only created once
  // We use it across multiple components in wp-react-scripts
  if (!window.globalGrowthBook) {
    window.globalGrowthBook = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      clientKey: GROWTHBOOK_CLIENT_KEY!,
      enableDevMode: true,
      disableUrlRedirectExperiments: true,
      disableCrossOriginUrlRedirectExperiments: true,
      // Only required for A/B testing
      // Called every time a user is put into an experiment
      trackingCallback: (experiment, result) => {
        if (!isProduction) {
          // eslint-disable-next-line no-console
          console.log('Experiment Viewed', {
            experimentId: experiment.key,
            variationId: result.key,
          })
        }
        // Track unique per session
        const sessionId = getAmplitudeSessionId()
        const storageKey = `gb_viewed_${sessionId}`
        let viewedExperiments: Record<string, boolean> = {}
        try {
          viewedExperiments = JSON.parse(sessionStore.getString(storageKey) || '{}')
        } catch {
          viewedExperiments = {}
        }
        const experimentKey = `${experiment.key}_${result.key}`
        if (!viewedExperiments[experimentKey]) {
          viewedExperiments[experimentKey] = true
          sessionStore.setJSON(storageKey, viewedExperiments)
          sendAmplitudeEvent(AmplitudeEvents.EXPERIMENT_VIEWED, {
            experimentId: experiment.key,
            variationId: result.key,
          })
        }
      },
      attributes: {
        ...attributes,
        ...(additionalAttributes ? additionalAttributes : {}),
      },
      stickyBucketService: new BrowserCookieStickyBucketService({
        jsCookie: Cookies,
      }),
    })

    if (!window.globalGrowthBook.ready) {
      const afterInit = () => {
        delete window.earlyGrowthBook
        window.dispatchEvent(new CustomEvent('growthbook-ready'))
      }

      const isDisabled = isGrowthBookDisabled()
      if (isDisabled) {
        // If growthbook is disabled, we need to initialize it with an empty payload
        window.globalGrowthBook
          .init({
            payload: { features: getEnabledFeatures() },
            streaming: options?.streaming ?? false,
          })
          .then(() => {
            afterInit()
          })
      } else {
        let initSync = false

        const htmlFeatureFlags = window.__gbInitialPayload
        const cachedFeatureFlagsStringified = localStore?.getString('gbFeaturesCache')

        let cachedFeatureFlags

        try {
          cachedFeatureFlags = cachedFeatureFlagsStringified
            ? JSON.parse(cachedFeatureFlagsStringified)[0][1].data
            : undefined
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error, 'Error parsing cached feature flags')
        }

        const payload = cachedFeatureFlags || htmlFeatureFlags

        if (payload) {
          //Check if the payload is recent (less than 5 minutes old)
          const lastUpdatedDate = new Date(payload.dateUpdated)
          const now = Date.now()

          // Difference in milliseconds
          const diffMs = now - lastUpdatedDate.getTime()

          // 5 minutes in milliseconds
          const fiveMinutes = 5 * 60 * 1000

          //If the payload is recent, initialize synchronously
          //Otherwise go to growthbook server to get the latest data
          if (diffMs < fiveMinutes) {
            try {
              window.globalGrowthBook.initSync({
                payload,
                streaming: options?.streaming ?? false,
              })
              afterInit()
              initSync = true
            } catch (error) {}
          }
        }

        if (!initSync) {
          window.globalGrowthBook
            .init({
              streaming: options?.streaming ?? false,
            })
            .then(() => {
              afterInit()
            })
        }
      }
    }
  }

  // Update attributes when they change
  React.useEffect(() => {
    if (window.globalGrowthBook) {
      window.globalGrowthBook.setAttributes({
        ...attributes,
        ...(additionalAttributes ? additionalAttributes : {}),
      })
    }
  }, [attributes, additionalAttributes])

  // If GrowthBook isn't available, render children without the provider
  if (!window.globalGrowthBook) {
    // eslint-disable-next-line no-console
    console.error('GrowthBook not initialized')
    return <>{children}</>
  }

  return (
    <InternalGrowthBookProvider growthbook={window.globalGrowthBook}>
      {children}
    </InternalGrowthBookProvider>
  )
}

const noop = () => () => null

function isGrowthBookDisabled() {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    getCookie(GROWTHBOOK_DISABLE_ALL_FEATURES_COOKIE_NAME) === 'true' ||
    Boolean(getCookie(GROWTHBOOK_ENABLED_FEATURES_COOKIE_NAME))
  )
}

function getEnabledFeatures() {
  const featureCookie = getCookie(GROWTHBOOK_ENABLED_FEATURES_COOKIE_NAME)

  if (!featureCookie?.trim()) {
    return {}
  }

  try {
    const parsed = JSON.parse(featureCookie)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed
    }
    return {}
  } catch (error) {
    return {}
  }
}

export function useFeatureIsOn(id: string, latch = false): boolean {
  const internalFeatureValue = InternalUseFeatureIsOn(id)
  const featureValue = React.useSyncExternalStore(
    noop,
    () => internalFeatureValue,
    () => false,
  )
  const valueState = React.useRef(featureValue)

  return latch ? (valueState.current ||= featureValue) : featureValue
}

export function useFeatureValue<T extends JSONValue>(
  id: string,
  fallback: T,
  latch = false,
): WidenPrimitives<T> {
  const internalFeatureValue = InternalUseFeatureValue(id, fallback)
  const featureValue = React.useSyncExternalStore(
    noop,
    () => internalFeatureValue,
    () => fallback as WidenPrimitives<T>,
  )
  const valueState = React.useRef(featureValue)

  return latch ? (valueState.current ??= featureValue) : featureValue
}

export function useFeatureValueLoadable<T extends JSONValue>(
  id: string,
  latch = false,
): { isLoading: boolean; data: WidenPrimitives<T> | undefined } {
  const fallback = undefined as unknown as T
  const growthbook = InternalUseGrowthBook()
  const featureValue = InternalUseFeatureValue(id, fallback)

  // Subscribe to GrowthBook ready state changes
  const isGrowthBookReady = React.useSyncExternalStore(
    React.useCallback(
      callback => {
        if (!growthbook) {
          return () => {
            // No-op
          }
        }

        // Listen for our custom ready event
        const handleReady = () => {
          callback()
        }

        window.addEventListener('growthbook-ready', handleReady)

        // Also subscribe to GrowthBook's own subscription for other changes
        const unsubscribe = growthbook.subscribe(callback)

        return () => {
          window.removeEventListener('growthbook-ready', handleReady)
          unsubscribe()
        }
      },
      [growthbook],
    ),
    () => {
      const ready = growthbook?.ready ?? false
      return ready
    },
    () => false,
  )

  const isLoading = !isGrowthBookReady
  const data = isGrowthBookReady ? featureValue : (fallback as WidenPrimitives<T>)

  const valueState = React.useRef<{ isLoading: boolean; data: WidenPrimitives<T> | undefined }>({
    isLoading: true,
    data: undefined,
  })

  if (!isLoading && valueState.current.isLoading) {
    valueState.current = {
      isLoading: false,
      data: data,
    }
  }

  return latch ? valueState.current : { isLoading, data }
}
